import { useState } from "react";
import {
  LayerRegular, DeveloperBoardRegular, DatabaseRegular, PhoneRegular, GlobeRegular, FlashRegular, ShieldRegular, TranslateRegular, DataBarVerticalRegular, CloudRegular,
  ArrowRightRegular, ServerRegular, FlowRegular, BrainRegular, RadioButtonRegular, BookOpenRegular, CodeRegular, BranchRegular, PeopleRegular, NewsRegular,
  ChatRegular, SparkleRegular, AlertRegular, SearchRegular, MapPinRegular, ClockRegular, ChevronRightRegular, ArrowExportRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Layers = fluentIcon(LayerRegular);
const Cpu = fluentIcon(DeveloperBoardRegular);
const Database = fluentIcon(DatabaseRegular);
const Smartphone = fluentIcon(PhoneRegular);
const Globe = fluentIcon(GlobeRegular);
const Zap = fluentIcon(FlashRegular);
const Shield = fluentIcon(ShieldRegular);
const Languages = fluentIcon(TranslateRegular);
const BarChart3 = fluentIcon(DataBarVerticalRegular);
const Cloud = fluentIcon(CloudRegular);
const ArrowRight = fluentIcon(ArrowRightRegular);
const Server = fluentIcon(ServerRegular);
const Workflow = fluentIcon(FlowRegular);
const Brain = fluentIcon(BrainRegular);
const Radio = fluentIcon(RadioButtonRegular);
const BookOpen = fluentIcon(BookOpenRegular);
const Code = fluentIcon(CodeRegular);
const GitBranch = fluentIcon(BranchRegular);
const Users = fluentIcon(PeopleRegular);
const Newspaper = fluentIcon(NewsRegular);
const MessageCircle = fluentIcon(ChatRegular);
const Sparkles = fluentIcon(SparkleRegular);
const Bell = fluentIcon(AlertRegular);
const Search = fluentIcon(SearchRegular);
const MapPin = fluentIcon(MapPinRegular);
const Clock = fluentIcon(ClockRegular);
const ChevronRight = fluentIcon(ChevronRightRegular);
const ExternalLink = fluentIcon(ArrowExportRegular);

type Section = "overview" | "architecture" | "tech" | "ai" | "pipeline" | "database" | "mobile" | "web" | "infra" | "api" | "governance" | "admin";

const NAV: { id: Section; label: string; icon: any }[] = [
  { id: "overview", label: "Overview", icon: BookOpen },
  { id: "architecture", label: "Architecture", icon: Layers },
  { id: "tech", label: "Tech Stack", icon: Code },
  { id: "ai", label: "AI Services", icon: Brain },
  { id: "pipeline", label: "News Pipeline", icon: Workflow },
  { id: "database", label: "Database", icon: Database },
  { id: "mobile", label: "Mobile App", icon: Smartphone },
  { id: "web", label: "Web & Marketing", icon: Globe },
  { id: "infra", label: "Infrastructure", icon: Cloud },
  { id: "api", label: "API Reference", icon: Server },
  { id: "governance", label: "Governance & Rules", icon: Shield },
  { id: "admin", label: "Admin & Ops", icon: BarChart3 },
];

function Badge({ children, color = "blue" }: { children: string; color?: string }) {
  const c = color === "green" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    : color === "yellow" ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
    : color === "red" ? "bg-red-500/10 text-red-400 border-red-500/20"
    : color === "purple" ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
    : "bg-brand-blue/10 text-brand-blue border-brand-blue/20";
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${c} uppercase tracking-wider`}>{children}</span>;
}

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-slate-900/60 border border-slate-800 rounded-xl p-5 ${className}`}>{children}</div>;
}

function SectionTitle({ icon: Icon, children }: { icon: any; children: string }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-brand-blue/10 border border-brand-blue/20 flex items-center justify-center">
        <Icon size={20} className="text-brand-blue" />
      </div>
      <h2 className="text-xl font-bold text-slate-100">{children}</h2>
    </div>
  );
}

function FlowStep({ icon: Icon, title, desc, last = false }: { icon: any; title: string; desc: string; last?: boolean }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-lg bg-brand-blue/15 border border-brand-blue/25 flex items-center justify-center flex-shrink-0">
          <Icon size={14} className="text-brand-blue" />
        </div>
        {!last && <div className="w-px flex-1 bg-slate-800 my-1" />}
      </div>
      <div className={`pb-5 ${last ? "" : ""}`}>
        <div className="text-sm font-semibold text-slate-200">{title}</div>
        <div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-brand-blue">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

function TableRow({ cells, header = false }: { cells: string[]; header?: boolean }) {
  const Tag = header ? "th" : "td";
  return (
    <tr className={header ? "border-b border-slate-700" : "border-b border-slate-800/50 hover:bg-slate-800/20"}>
      {cells.map((c, i) => (
        <Tag key={i} className={`py-2.5 px-3 text-left text-xs ${header ? "text-slate-400 font-semibold uppercase tracking-wider" : i === 0 ? "text-slate-200 font-medium" : "text-slate-400"}`}>
          {c}
        </Tag>
      ))}
    </tr>
  );
}

// ─── Sections ──────────────────────────────────────────────────────────────────

function OverviewSection() {
  return (
    <div>
      <SectionTitle icon={BookOpen}>Tomitra Overview</SectionTitle>

      <Card className="mb-6">
        <div className="flex items-start gap-4 mb-4">
          <img src="/logo.png" alt="Tomitra" className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />
          <div>
            <h3 className="text-lg font-bold text-slate-100">Tomitra</h3>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">Tomitra is an AI-powered news aggregation and intelligence platform built for India. It aggregates news from 42+ RSS feeds and the GNews API, uses Gemini AI to create InShorts-style 60-75 word summaries with smart categorization, translates everything to Hindi and 11 Indian regional languages using a self-hosted NLLB-200 neural translation engine, and provides interactive AI features including conversational chat about articles, visual insight cards, daily news briefings, and hyperlocal GPS-based news for 12+ Indian cities. The entire platform runs on Google Cloud (Mumbai region) with self-hosted LLM inference for cost efficiency.</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-800">
          <Stat label="Articles" value="13K+" sub="in database" />
          <Stat label="Languages" value="12" sub="Hindi + 11 regional" />
          <Stat label="AI Features" value="7" sub="summarize, chat, insights..." />
          <Stat label="News Sources" value="42+" sub="RSS feeds configured" />
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4">
          <Stat label="Monthly Cost" value="~$75" sub="all infrastructure" />
          <Stat label="Categories" value="20" sub="top, sports, she..." />
          <Stat label="Cities" value="12+" sub="hyperlocal coverage" />
          <Stat label="Platforms" value="5" sub="iOS, Android, Web, Admin, API" />
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-2 mb-3"><Newspaper size={16} className="text-brand-blue" /><span className="text-sm font-semibold text-slate-200">Smart Summaries</span></div>
          <p className="text-xs text-slate-400 leading-relaxed">Every article is processed by Gemini 2.5 Flash-Lite to generate a 5-8 word headline, a 60-75 word summary, automatic category assignment (from 20 categories), up to 5 tags, sentiment score, and key entity extraction. Word count is enforced in code with retry and padding logic to guarantee consistent card sizes across the feed.</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 mb-3"><Languages size={16} className="text-brand-blue" /><span className="text-sm font-semibold text-slate-200">Multilingual (12 Languages)</span></div>
          <p className="text-xs text-slate-400 leading-relaxed">A self-hosted Meta NLLB-200 (600M parameter) neural translation model runs on Cloud Run Mumbai. It translates titles and summaries to Hindi, Tamil, Bengali, Marathi, Telugu, Gujarati, Kannada, Malayalam, Punjabi, Urdu, Odia, and Assamese. All translations are validated for correct script (Devanagari, Tamil, etc.) with automatic fallback to Gemini if quality is insufficient.</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 mb-3"><MessageCircle size={16} className="text-brand-blue" /><span className="text-sm font-semibold text-slate-200">Ask Tomitra (AI Chat)</span></div>
          <p className="text-xs text-slate-400 leading-relaxed">Users can ask questions about any article and receive real-time streaming AI responses (30-45 words). Each response includes 3 contextual follow-up question suggestions. Supports both English and Hindi (Devanagari). Responses are cached (LRU, 6h TTL) for instant repeat queries. Identity: "I'm Tomitra, your intelligent news companion."</p>
        </Card>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-2 mb-3"><Sparkles size={16} className="text-purple-400" /><span className="text-sm font-semibold text-slate-200">Visual Insights</span></div>
          <p className="text-xs text-slate-400 leading-relaxed">Each article gets 5 AI-generated explainer cards: headline overview, key facts, background context, why it matters, and what happens next. Cards include emoji icons and are cached in the database per article per language. Hindi cards are generated by translating the English version via batch NLLB translation for speed.</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 mb-3"><Radio size={16} className="text-emerald-400" /><span className="text-sm font-semibold text-slate-200">Daily Briefing</span></div>
          <p className="text-xs text-slate-400 leading-relaxed">AI generates broadcast-style news scripts with 5-8 article segments, greeting, transitions, and sign-off. Morning and evening variants available in English and Hindi. Cached for 12 hours in the database. Push notifications sent at 8 AM IST daily.</p>
        </Card>
        <Card>
          <div className="flex items-center gap-2 mb-3"><MapPin size={16} className="text-yellow-400" /><span className="text-sm font-semibold text-slate-200">Hyperlocal News</span></div>
          <p className="text-xs text-slate-400 leading-relaxed">GPS-based local news feed showing articles from the user's city, district, and neighbourhood. Uses haversine distance scoring combined with recency weighting (1 hour of age = 2km virtual distance). Articles older than 7 days are excluded. Covers 12+ Indian cities with pre-configured RSS feeds containing embedded GPS coordinates.</p>
        </Card>
      </div>
    </div>
  );
}

function ArchitectureSection() {
  return (
    <div>
      <SectionTitle icon={Layers}>System Architecture</SectionTitle>

      {/* System Architecture Flow Diagram */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">System Architecture Flow</h3>
        <p className="text-[10px] text-slate-500 mb-5">Complete request flow from news sources through processing to end users.</p>

        {/* Row 1: Data Sources */}
        <div className="mb-4">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Data Sources</div>
          <div className="flex justify-center gap-3">
            {[
              { icon: Globe, label: "GNews API", sub: "20 articles/call", color: "text-yellow-400", bg: "border-yellow-800/40" },
              { icon: Radio, label: "51 RSS Feeds", sub: "510 articles/pull", color: "text-orange-400", bg: "border-orange-800/40" },
            ].map((s, i) => (
              <div key={i} className={`flex items-center gap-2 bg-slate-800/50 border ${s.bg} rounded-lg px-4 py-2.5`}>
                <s.icon size={16} className={s.color} />
                <div><div className="text-xs font-semibold text-slate-200">{s.label}</div><div className="text-[10px] text-slate-500">{s.sub}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center mb-4"><div className="w-px h-6 bg-slate-700" /><ArrowRight size={10} className="text-slate-600 rotate-90 -ml-[5px]" /></div>

        {/* Row 2: Processing Layer */}
        <div className="mb-4">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Processing Layer (Cloud Run Mumbai)</div>
          <div className="border border-brand-blue/20 bg-brand-blue/5 rounded-xl p-4">
            <div className="flex justify-center gap-3 mb-3">
              {[
                { icon: Server, label: "Backend API", sub: "Fastify + TypeScript", color: "text-brand-blue" },
                { icon: Brain, label: "Gemini 2.5 Flash", sub: "Summarize + Chat + Insights", color: "text-purple-400" },
                { icon: Languages, label: "NLLB-200", sub: "Hindi Translation", color: "text-emerald-400" },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-800/80 rounded-lg px-3 py-2">
                  <s.icon size={14} className={s.color} />
                  <div><div className="text-[10px] font-semibold text-slate-200">{s.label}</div><div className="text-[10px] text-slate-500">{s.sub}</div></div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-3">
              {[
                { icon: Brain, label: "Phi-3.5 LLM", sub: "AI Fallback", color: "text-pink-400" },
                { icon: Shield, label: "Firebase Auth", sub: "Google/Apple/Phone", color: "text-yellow-400" },
                { icon: Bell, label: "Firebase FCM", sub: "Push Notifications", color: "text-red-400" },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-800/80 rounded-lg px-3 py-2">
                  <s.icon size={14} className={s.color} />
                  <div><div className="text-[10px] font-semibold text-slate-200">{s.label}</div><div className="text-[10px] text-slate-500">{s.sub}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-center mb-4"><div className="w-px h-6 bg-slate-700" /></div>

        {/* Row 3: Storage */}
        <div className="mb-4">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Storage Layer</div>
          <div className="flex justify-center">
            <div className="flex items-center gap-2 bg-emerald-950/30 border border-emerald-800/40 rounded-lg px-6 py-3">
              <Database size={18} className="text-emerald-400" />
              <div><div className="text-xs font-semibold text-slate-200">Cloud SQL PostgreSQL 15</div><div className="text-[10px] text-slate-500">13K+ articles, 20+ tables, FTS indexes, JSONB translations</div></div>
            </div>
          </div>
        </div>
        <div className="flex justify-center mb-4"><div className="w-px h-6 bg-slate-700" /></div>

        {/* Row 4: Delivery */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Delivery Layer</div>
          <div className="flex justify-center gap-3">
            {[
              { icon: Smartphone, label: "Mobile App", sub: "iOS + Android (Expo)", color: "text-pink-400", bg: "border-pink-800/40" },
              { icon: Globe, label: "tomitra.com", sub: "Marketing + Strategy", color: "text-brand-blue", bg: "border-brand-blue/30" },
              { icon: BarChart3, label: "admin.tomitra.com", sub: "Ops Dashboard", color: "text-purple-400", bg: "border-purple-800/40" },
            ].map((s, i) => (
              <div key={i} className={`flex items-center gap-2 bg-slate-800/50 border ${s.bg} rounded-lg px-4 py-2.5`}>
                <s.icon size={16} className={s.color} />
                <div><div className="text-xs font-semibold text-slate-200">{s.label}</div><div className="text-[10px] text-slate-500">{s.sub}</div></div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Request flow */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-4">User Request Flow</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Read Flow (Feed, Search, Local)</div>
            <FlowStep icon={Smartphone} title="User opens app" desc="Feed tab loads, sends GET /api/v1/feed with category, language, location params." />
            <FlowStep icon={Shield} title="Auth check" desc="Firebase ID token verified. Profile auto-created on first request." />
            <FlowStep icon={Database} title="Query articles" desc="PostgreSQL full-text search with tsvector. Haversine distance scoring for local. Cursor pagination." />
            <FlowStep icon={Smartphone} title="Render cards" desc="60-75 word summaries displayed as swipeable cards. Hindi if user preference." last />
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">AI Flow (Chat, Insights)</div>
            <FlowStep icon={Smartphone} title="User taps 'Ask Tomitra'" desc="Opens chat sheet. Sends POST /api/v1/articles/:id/chat/stream." />
            <FlowStep icon={Brain} title="AI cascade" desc="Gemini 2.5 Flash-Lite only (as of 2026-07-27) — Anthropic fallback disabled (no credit), Phi-3.5 not deployed to prod." />
            <FlowStep icon={Zap} title="Streaming response" desc="SSE tokens streamed in real-time. 30-45 words + 3 follow-up questions." />
            <FlowStep icon={Database} title="Cache response" desc="LRU cache (1000 entries, 6h TTL). Same question returns instantly." last />
          </div>
        </div>
      </Card>

      {/* Monorepo structure */}
      <Card>
        <h3 className="text-sm font-semibold text-slate-300 mb-4">Monorepo Structure</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Applications</div>
            {[
              { name: "apps/mobile", desc: "React Native Expo (iOS + Android)", badge: "Production" },
              { name: "apps/backend", desc: "Node.js Fastify API server", badge: "Cloud Run" },
              { name: "apps/translator", desc: "Python FastAPI NLLB-200", badge: "Cloud Run" },
              { name: "apps/inference-phi", desc: "Phi-3.5 LLM server", badge: "Cloud Run" },
              { name: "apps/admin", desc: "React admin dashboard", badge: "Firebase" },
              { name: "apps/web", desc: "Marketing website", badge: "Firebase" },
            ].map(a => (
              <div key={a.name} className="flex items-center justify-between py-2 border-b border-slate-800/50">
                <div>
                  <code className="text-xs text-brand-blue font-mono">{a.name}/</code>
                  <div className="text-[10px] text-slate-500">{a.desc}</div>
                </div>
                <Badge color="green">{a.badge}</Badge>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Shared Packages</div>
            {[
              { name: "packages/shared", desc: "Types, validators, constants, geocoding" },
              { name: "packages/ai-engine", desc: "LLM integration, summarizer, translator, chat" },
              { name: "packages/news-ingestion", desc: "GNews + RSS fetchers, deduplication" },
            ].map(a => (
              <div key={a.name} className="flex items-center justify-between py-2 border-b border-slate-800/50">
                <div>
                  <code className="text-xs text-purple-400 font-mono">{a.name}/</code>
                  <div className="text-[10px] text-slate-500">{a.desc}</div>
                </div>
              </div>
            ))}
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 mt-4">Config</div>
            {[
              { name: "cloudbuild.yaml", desc: "GCP Cloud Build (backend Docker)" },
              { name: ".gcloudignore", desc: "Build context exclusions" },
              { name: "tsconfig.base.json", desc: "ES2022, strict TypeScript" },
            ].map(a => (
              <div key={a.name} className="flex items-center py-2 border-b border-slate-800/50">
                <div>
                  <code className="text-xs text-slate-400 font-mono">{a.name}</code>
                  <div className="text-[10px] text-slate-500">{a.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function TechStackSection() {
  return (
    <div>
      <SectionTitle icon={Code}>Tech Stack</SectionTitle>
      <div className="grid grid-cols-2 gap-4">
        {[
          { title: "Mobile", items: ["React Native (Expo SDK 54)", "TypeScript + Expo Router v6", "NativeWind 4.x + Tailwind CSS", "Zustand + TanStack Query", "Firebase Auth (native)", "Reanimated 4 + Gesture Handler", "i18next (EN + HI)"], color: "text-pink-400", icon: Smartphone },
          { title: "Backend", items: ["Node.js 20 + Fastify 5", "TypeScript (ESM, tsx runner)", "postgres tagged-template SQL", "node-cron (scheduler)", "Pino (logging)", "Zod (validation)"], color: "text-brand-blue", icon: Server },
          { title: "AI & ML", items: ["Gemini 2.5 Flash-Lite (primary)", "Phi-3.5-mini self-hosted (fallback)", "NLLB-200 self-hosted (translation)", "llama-cpp-python (inference)", "Circuit breaker cascade", "AI call metrics logging"], color: "text-purple-400", icon: Brain },
          { title: "Infrastructure", items: ["GCP Cloud Run (Mumbai)", "Cloud SQL PostgreSQL 15", "Firebase Auth + FCM + Hosting", "Artifact Registry (Docker)", "Secret Manager", "Cloud Build CI/CD"], color: "text-emerald-400", icon: Cloud },
        ].map(stack => (
          <Card key={stack.title}>
            <div className="flex items-center gap-2 mb-4">
              <stack.icon size={18} className={stack.color} />
              <h3 className="text-sm font-bold text-slate-200">{stack.title}</h3>
            </div>
            <div className="space-y-2">
              {stack.items.map(item => (
                <div key={item} className="flex items-center gap-2 text-xs text-slate-400">
                  <ChevronRight size={10} className="text-slate-600 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AISection() {
  return (
    <div>
      <SectionTitle icon={Brain}>AI Services</SectionTitle>

      {/* Provider cascade */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-4">Provider Cascade</h3>
        <p className="text-xs text-slate-400 mb-4">All AI calls route through a priority cascade with circuit breaker protection. If the primary fails, the next provider picks up automatically.</p>
        <div className="space-y-0">
          <FlowStep icon={Zap} title="Priority 1: Gemini 2.5 Flash-Lite" desc="~2s response, pay-as-you-go ($0.15/M tokens), excellent instruction following. Handles 99% of requests." />
          <FlowStep icon={Cpu} title="Priority 2: Tomitra Phi-3.5 (Self-hosted)" desc="~30-50s on CPU, always-on Cloud Run service. Zero per-request cost. Fallback when Gemini is down." />
          <FlowStep icon={Shield} title="Priority 3: Anthropic Claude" desc="Last resort. Native SDK, used only if both primary providers fail." last />
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 mt-4">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Circuit Breaker</div>
          <div className="text-xs text-slate-400">30min cooldown after auth/quota failures (401/403). No cooldown after rate limits (429, changed 2026-08-04) — every call attempts the provider directly since shared-quota availability is per-request, not a "provider is down" signal. Auto-recovers.</div>
        </div>
      </Card>

      {/* AI Features grid */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { title: "Article Summarization", desc: "60-75 word InShorts-style summaries with short title, category, tags, sentiment. Code enforces word count with retry + padding.", badge: "Every article", model: "Gemini 2.5 Flash-Lite" },
          { title: "Ask Tomitra (Chat)", desc: "Streaming Q&A about any article. 30-45 word answers with 3 follow-up suggestions. Supports English and Hindi.", badge: "On demand", model: "Gemini 2.5 Flash-Lite" },
          { title: "Visual Insights", desc: "5 explanatory cards: headline, key facts, context, why it matters, what's next. Cached per article per language.", badge: "On demand", model: "Gemini 2.5 Flash-Lite" },
          { title: "Hindi Translation", desc: "Self-hosted NLLB-200 (600M params). max_new_tokens=512. Validates Devanagari script. Gemini fallback if too short.", badge: "Every article", model: "NLLB-200 (self-hosted)" },
          { title: "Daily Briefing", desc: "News anchor-style scripts with 5-8 segments. Morning/evening variants. Cached 12h.", badge: "2x daily", model: "Gemini 2.5 Flash-Lite" },
          { title: "Trending Topics", desc: "Analyzes 80 headlines to extract 3 trending topics with labels, emoji, heat score. 2h cache.", badge: "On demand", model: "Gemini 2.5 Flash-Lite" },
        ].map(f => (
          <Card key={f.title}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-200">{f.title}</h4>
              <Badge color="green">{f.badge}</Badge>
            </div>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">{f.desc}</p>
            <div className="text-[10px] text-slate-500">Model: <span className="text-slate-400">{f.model}</span></div>
          </Card>
        ))}
      </div>

      {/* Content quality rules */}
      <Card className="mt-6">
        <h3 className="text-sm font-semibold text-red-400 mb-3">Content Quality Rules (Non-Negotiable)</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-300 mb-1">English Summary</div>
            <div className="text-[10px] text-slate-400">60-75 words, 60 is a hard floor. Period at end. No ellipsis. Code rejects &lt;60 words, retries, then pads from the publisher text. Hindi carries the same 60-word floor.</div>
          </div>
          <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-300 mb-1">Title</div>
            <div className="text-[10px] text-slate-400">10-15 words, two lines. No clickbait, no questions. Enforced in code via enforceTitle().</div>
          </div>
          <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-300 mb-1">Hindi Translation</div>
            <div className="text-[10px] text-slate-400">Min 50% of English word count. Pure Devanagari. NLLB max_new_tokens=512. Gemini fallback if short.</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function PipelineSection() {
  return (
    <div>
      <SectionTitle icon={Workflow}>News Pipeline</SectionTitle>

      {/* Ingestion Architecture Diagram */}
      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">Ingestion Architecture</h3>
        <p className="text-[10px] text-slate-500 mb-5">Every 15 minutes, the cron scheduler triggers the ingestion pipeline. Articles flow from sources through AI processing to the database.</p>

        {/* Sources row */}
        <div className="mb-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Step 1: Fetch (Parallel)</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-yellow-950/20 border border-yellow-900/30 rounded-lg p-3 text-center">
              <Globe size={18} className="text-yellow-400 mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">GNews Top Headlines</div>
              <div className="text-[10px] text-slate-500">20 articles, India, English</div>
            </div>
            <div className="bg-orange-950/20 border border-orange-900/30 rounded-lg p-3 text-center">
              <Globe size={18} className="text-orange-400 mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">GNews Category (rotating)</div>
              <div className="text-[10px] text-slate-500">20 articles, cycles through 9 categories</div>
            </div>
            <div className="bg-emerald-950/20 border border-emerald-900/30 rounded-lg p-3 text-center">
              <Radio size={18} className="text-emerald-400 mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">51 RSS Feeds</div>
              <div className="text-[10px] text-slate-500">Up to 510 articles from major Indian sources</div>
            </div>
          </div>
        </div>
        <div className="flex justify-center my-2 text-[10px] text-slate-500">Combined: ~550 raw articles per run</div>
        <div className="flex justify-center mb-3"><div className="w-px h-4 bg-slate-700" /></div>

        {/* Dedup */}
        <div className="mb-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Step 2: Deduplicate</div>
          <div className="flex justify-center">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg px-6 py-3 text-center max-w-md">
              <GitBranch size={16} className="text-brand-blue mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">SHA-256 Content Hash</div>
              <div className="text-[10px] text-slate-500">Hash = title + first 200 chars of content. In-memory dedup across all sources, then DB check before AI call. Skips articles already in database instantly.</div>
            </div>
          </div>
        </div>
        <div className="flex justify-center my-2 text-[10px] text-slate-500">~100 unique articles pass through (capped at 100)</div>
        <div className="flex justify-center mb-3"><div className="w-px h-4 bg-slate-700" /></div>

        {/* Processing */}
        <div className="mb-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Step 3: AI Processing (per article, 500ms apart)</div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { icon: Brain, label: "AI Summarize", desc: "Gemini: title (5-8w), summary (60-75w), category, tags, sentiment", color: "text-purple-400", bg: "bg-purple-950/20 border-purple-900/30" },
              { icon: Languages, label: "Hindi Translate", desc: "NLLB-200: title + summary to Devanagari. Script validated.", color: "text-emerald-400", bg: "bg-emerald-950/20 border-emerald-900/30" },
              { icon: MapPin, label: "Geocode", desc: "Extract city, state, lat/lng from AI-detected locations", color: "text-yellow-400", bg: "bg-yellow-950/20 border-yellow-900/30" },
              { icon: Search, label: "OG Image", desc: "Fetch article page, extract og:image meta tag", color: "text-brand-blue", bg: "bg-brand-blue/5 border-brand-blue/20" },
            ].map((s, i) => (
              <div key={i} className={`${s.bg} border rounded-lg p-2.5 text-center`}>
                <s.icon size={14} className={`${s.color} mx-auto mb-1`} />
                <div className="text-[10px] font-semibold text-slate-200">{s.label}</div>
                <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center mb-3"><div className="w-px h-4 bg-slate-700" /></div>

        {/* Store + Notify */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 text-center">Step 4: Store & Notify</div>
          <div className="flex justify-center gap-3">
            <div className="bg-emerald-950/20 border border-emerald-900/30 rounded-lg px-4 py-2.5 text-center">
              <Database size={16} className="text-emerald-400 mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">Cloud SQL</div>
              <div className="text-[10px] text-slate-500">INSERT with ON CONFLICT DO NOTHING</div>
            </div>
            <div className="bg-red-950/20 border border-red-900/30 rounded-lg px-4 py-2.5 text-center">
              <Bell size={16} className="text-red-400 mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">Breaking News</div>
              <div className="text-[10px] text-slate-500">FCM push if recent + top category</div>
            </div>
            <div className="bg-brand-blue/5 border border-brand-blue/20 rounded-lg px-4 py-2.5 text-center">
              <BarChart3 size={16} className="text-brand-blue mx-auto mb-1" />
              <div className="text-xs font-semibold text-slate-200">Log Metrics</div>
              <div className="text-[10px] text-slate-500">ingestion_logs + ai_call_logs</div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Card>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Cron Schedule</h3>
          <table className="w-full"><tbody>
            <TableRow cells={["Job", "Schedule", "Purpose"]} header />
            <TableRow cells={["News Ingestion", "Every 15 min", "Fetch + summarize + translate (up to 100 articles)"]} />
            <TableRow cells={["Morning Briefing", "8:00 AM IST", "Push notification with AI-generated script"]} />
            <TableRow cells={["Trending (Midday)", "12:00 PM IST", "Push top trending topic"]} />
            <TableRow cells={["Trending (Evening)", "6:00 PM IST", "Push evening trending"]} />
            <TableRow cells={["Startup Ingestion", "30s after boot", "Immediate fetch after deploy"]} />
          </tbody></table>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">GNews Category Rotation</h3>
          <p className="text-xs text-slate-400 mb-2">Each ingestion run picks the next category. Full rotation every ~2.25 hours, covering all news verticals:</p>
          <div className="flex flex-wrap gap-1.5">
            {["breaking-news", "nation", "world", "business", "technology", "sports", "entertainment", "science", "health"].map((c, i) => (
              <span key={c} className="text-[10px] bg-slate-800 px-2.5 py-1 rounded-full text-slate-400">
                <span className="text-brand-blue font-mono mr-1">{i+1}</span>{c}
              </span>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Card>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">News Sources (Active)</h3>
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2"><Badge color="blue">GNews API</Badge><span className="text-xs text-slate-400">2 calls/run (top + rotating category), 100 req/day free</span></div>
            <div className="flex items-center gap-2"><Badge color="green">51 RSS Feeds</Badge><span className="text-xs text-slate-400">Active, fetched in parallel every 15 min</span></div>
          </div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Major RSS Sources</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {["NDTV (5)", "Times of India (10)", "Hindustan Times (9)", "The Hindu (2)", "Indian Express", "India Today", "The Print", "Economic Times (2)"].map(s => (
              <span key={s} className="text-[10px] bg-slate-800 px-2.5 py-1 rounded-full text-slate-400">{s}</span>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">City-Level Feeds (with GPS)</div>
          <div className="flex flex-wrap gap-1.5">
            {["Delhi", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Lucknow", "Chandigarh", "Pune", "Jaipur", "Patna", "Noida"].map(s => (
              <span key={s} className="text-[10px] bg-emerald-900/30 border border-emerald-800/30 px-2.5 py-1 rounded-full text-emerald-400">{s}</span>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Push Notifications</h3>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Delivery</div>
          <div className="text-xs text-slate-400 space-y-1 mb-3">
            <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Firebase Cloud Messaging v1 (primary)</div>
            <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Expo Push (fallback for ExponentPushToken)</div>
            <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Quiet hours: 10 PM - 7 AM IST</div>
          </div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Types</div>
          <div className="text-xs text-slate-400 space-y-1">
            <div className="flex items-center gap-2"><Bell size={10} className="text-red-400" />Breaking News (auto-detected)</div>
            <div className="flex items-center gap-2"><Bell size={10} className="text-yellow-400" />Morning Briefing (8 AM IST)</div>
            <div className="flex items-center gap-2"><Bell size={10} className="text-brand-blue" />Trending Topics (12 PM + 6 PM)</div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DatabaseSection() {
  return (
    <div>
      <SectionTitle icon={Database}>Database Schema</SectionTitle>
      <p className="text-xs text-slate-400 mb-4">Cloud SQL PostgreSQL 15 (Mumbai). Instance: tomitra-db. 20+ tables.</p>

      <div className="space-y-4">
        {[
          { name: "articles", rows: "13K+", cols: ["id UUID", "title TEXT", "summary_en TEXT", "summaries JSONB", "category TEXT", "tags TEXT[]", "location_* (city, state, lat, lng)", "content_hash TEXT", "fts TSVECTOR"], desc: "Core news table. Full-text search, geo-indexed." },
          { name: "profiles", rows: "~12", cols: ["id TEXT (Firebase UID)", "display_name", "email", "phone", "preferred_language", "location_*", "mood_preference", "push_token"], desc: "User accounts. Linked to Firebase Auth." },
          { name: "visual_insights", rows: "Cached", cols: ["article_id UUID", "language TEXT", "topic", "headline", "cards JSONB", "view_count"], desc: "AI insight cards per article per language." },
          { name: "ai_call_logs", rows: "Growing", cols: ["provider TEXT", "model TEXT", "feature TEXT", "status", "latency_ms INT", "total_tokens INT", "error_msg"], desc: "Every AI/LLM call logged for monitoring." },
          { name: "ingestion_logs", rows: "Growing", cols: ["status TEXT", "duration_ms", "articles_ingested", "hindi_translated", "sources JSONB", "errors JSONB"], desc: "Every cron run with detailed breakdown." },
        ].map(t => (
          <Card key={t.name}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-brand-blue font-semibold">{t.name}</code>
                <Badge>{t.rows} rows</Badge>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-3">{t.desc}</p>
            <div className="flex flex-wrap gap-1.5">
              {t.cols.map(c => (
                <code key={c} className="text-[10px] bg-slate-800 px-2 py-0.5 rounded text-slate-400 font-mono">{c}</code>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MobileSection() {
  return (
    <div>
      <SectionTitle icon={Smartphone}>Mobile App</SectionTitle>

      <Card className="mb-6">
        <div className="grid grid-cols-4 gap-4 mb-4">
          <Stat label="Version" value="1.4.1" />
          <Stat label="Expo SDK" value="54" />
          <Stat label="Platforms" value="2" sub="iOS + Android" />
          <Stat label="Screens" value="37" sub="TSX files" />
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">5 Tab Navigation</h3>
          {[
            { tab: "Feed", desc: "Swipeable news cards, category filters, For You / India / Sports / Tech" },
            { tab: "Discover", desc: "Category grid, trending topics, full-text search" },
            { tab: "Local", desc: "GPS-based hyperlocal news (24h window)" },
            { tab: "Briefing", desc: "AI-generated daily news summary" },
            { tab: "Profile", desc: "Account, preferences, language, theme, feedback" },
          ].map(t => (
            <div key={t.tab} className="flex gap-3 py-2 border-b border-slate-800/50">
              <span className="text-xs font-semibold text-brand-blue w-16">{t.tab}</span>
              <span className="text-xs text-slate-400">{t.desc}</span>
            </div>
          ))}
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Key Features</h3>
          {[
            { feat: "Ask Tomitra", desc: "AI chat about any article (streaming)" },
            { feat: "Visual Insights", desc: "5-card explainer carousel" },
            { feat: "Language Toggle", desc: "Triple-tap feed tab: EN/HI" },
            { feat: "Dark Mode", desc: "Triple-tap profile tab" },
            { feat: "Push Notifications", desc: "Breaking news, briefing, trending" },
            { feat: "Bookmarks & Likes", desc: "Synced via backend API" },
            { feat: "Tomitra Radio", desc: "Discover tab — Bodh/Top 10/Trending, continuous auto-advancing playback, single voice (Asha)" },
            { feat: "Global Coverage", desc: "Discover tab — how BBC/Al Jazeera/CNN/etc. cover India, live favicon attribution" },
            { feat: "From Social Media", desc: "Discover tab — curated video clips from 2 pilot channels via apps/social-engine" },
          ].map(f => (
            <div key={f.feat} className="flex gap-3 py-2 border-b border-slate-800/50">
              <span className="text-xs font-semibold text-slate-300 w-28">{f.feat}</span>
              <span className="text-xs text-slate-400">{f.desc}</span>
            </div>
          ))}
        </Card>
      </div>

      <Card>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Auth Flow</h3>
        <FlowStep icon={Shield} title="Firebase Auth" desc="Google Sign-In, Apple Sign-In, Phone OTP (+91). Native SDK via @react-native-firebase/auth." />
        <FlowStep icon={Users} title="Profile Setup" desc="Name, email, phone, gender. Saved to backend via API." />
        <FlowStep icon={MapPin} title="Onboarding" desc="Location permission for local news feed." />
        <FlowStep icon={Zap} title="Session" desc="Firebase ID token in Zustand store. Auto-refreshed. Backend verifies via Firebase Admin SDK." last />
      </Card>
    </div>
  );
}

function WebSection() {
  return (
    <div>
      <SectionTitle icon={Globe}>Web & Marketing</SectionTitle>

      <Card className="mb-6">
        <p className="text-[10px] text-slate-500 mb-4">
          2026-08-01: this used to describe a single `apps/web` marketing site — that app no longer exists in the repo. It split into three separate apps, each its own Firebase Hosting site.
        </p>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/logo.png" alt="Tomitra" className="w-10 h-10 rounded-xl object-cover" />
              <div>
                <h3 className="text-sm font-bold text-slate-200">tomitra.com</h3>
                <p className="text-[10px] text-slate-500">Corporate / marketing site</p>
              </div>
            </div>
            <div className="space-y-2 text-xs text-slate-400">
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />apps/corporate/ — React + Vite</div>
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Firebase project: tomitra-corp-prod</div>
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />/privacy, /terms, registered company identity</div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Newspaper size={18} className="text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-200">news.tomitra.com</h3>
                <p className="text-[10px] text-slate-500">Live news reader (public)</p>
              </div>
            </div>
            <div className="space-y-2 text-xs text-slate-400">
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />apps/news/ — React + Vite + react-router</div>
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Firebase project: tomitra-prod (default site)</div>
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Feed, /bodh briefing, /radio, Global Coverage, Ask Tomitra chat</div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                <BarChart3 size={18} className="text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-200">admin.tomitra.com</h3>
                <p className="text-[10px] text-slate-500">Admin dashboard (this site)</p>
              </div>
            </div>
            <div className="space-y-2 text-xs text-slate-400">
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />apps/admin/ — React + Vite + Tailwind</div>
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Firebase project: tomitra-corp-prod, site tomitra-admin-panel</div>
              <div className="flex items-center gap-2"><ChevronRight size={10} className="text-slate-600" />Auth runs against tomitra-prod (Firebase Auth, email allowlist)</div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-3">news.tomitra.com Pages</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { path: "/", desc: "Live feed reader (EN/HI on-demand translation)" },
            { path: "/bodh", desc: "Daily briefing (आज का बोध), neural narration" },
            { path: "/radio", desc: "Tomitra Radio — Bodh/Top 10/Trending, continuous playback" },
            { path: "/story/:id", desc: "Shareable article page + related stories" },
            { path: "/search", desc: "Full-text search (public)" },
            { path: "/me", desc: "Saved bookmarks + history, sign-out" },
          ].map(p => (
            <div key={p.path} className="flex gap-3 py-2 border-b border-slate-800/50">
              <code className="text-xs text-brand-blue font-mono w-20 flex-shrink-0">{p.path}</code>
              <span className="text-xs text-slate-400">{p.desc}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-yellow-400 mb-3">Strategy Vault (Founder Only)</h3>
        <p className="text-xs text-slate-400 mb-3">Password-protected confidential strategy page. Not linked from main navigation.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-yellow-950/20 border border-yellow-900/30 rounded-lg p-3">
            <div className="text-xs text-slate-300 mb-1">Access Methods</div>
            <div className="text-[10px] text-slate-400 space-y-1">
              <div>URL: tomitra.com/strategy</div>
              <div>Shortcut: Ctrl+Shift+S (anywhere on site)</div>
              <div>Easter egg: type "strategy" on any page</div>
            </div>
          </div>
          <div className="bg-yellow-950/20 border border-yellow-900/30 rounded-lg p-3">
            <div className="text-xs text-slate-300 mb-1">Contains</div>
            <div className="text-[10px] text-slate-400 space-y-1">
              <div>13 sections: Executive Summary, AI-Era Lifecycle, Portfolio Analysis, Three-Horizon Plan, Timing Window, Risks, Success Metrics, and more</div>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Deployment</h3>
        <div className="bg-slate-800/50 rounded-lg p-3 font-mono text-xs text-slate-400 space-y-1">
          <div><span className="text-slate-500"># Corporate (tomitra.com)</span></div>
          <div>cd apps/corporate && npm run build</div>
          <div>firebase deploy --only hosting --project tomitra-corp-prod</div>
          <div className="pt-2"><span className="text-slate-500"># News (news.tomitra.com)</span></div>
          <div>cd apps/news && npm run build</div>
          <div>firebase deploy --only hosting --project tomitra-prod</div>
          <div className="pt-2"><span className="text-slate-500"># Admin (admin.tomitra.com)</span></div>
          <div>cd apps/admin && npm run build</div>
          <div>firebase deploy --only hosting --project tomitra-corp-prod</div>
        </div>
      </Card>
    </div>
  );
}

function InfraSection() {
  return (
    <div>
      <SectionTitle icon={Cloud}>Infrastructure</SectionTitle>

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-4">GCP Services (asia-south1 / Mumbai)</h3>
        <table className="w-full"><tbody>
          <TableRow cells={["Service", "Type", "Config", "Cost/mo"]} header />
          <TableRow cells={["tomitra-backend", "Cloud Run", "Auto-scale, Cloud SQL proxy", "~$7"]} />
          <TableRow cells={["tomitra-translator", "Cloud Run", "4GB RAM, min-instances=1", "~$30"]} />
          <TableRow cells={["tomitra-inference-phi", "Cloud Run", "8GB RAM, 8 vCPU, min-instances=1", "~$35"]} />
          <TableRow cells={["tomitra-db", "Cloud SQL", "Postgres 15, SSD, daily backups", "~$15"]} />
          <TableRow cells={["Firebase Auth/FCM/Hosting", "Firebase", "Free tier", "$0"]} />
          <TableRow cells={["Gemini API", "Google AI", "Pay-as-you-go, $0.15/M tokens", "~$0.50"]} />
        </tbody></table>
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-800">
          <span className="text-xs text-slate-500">Total estimated monthly</span>
          <span className="text-lg font-bold text-brand-blue">~$75-105</span>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">GCP Project Details</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Project ID", "tomitra-prod"],
            ["Project Number", "187545700403"],
            ["Region", "asia-south1 (Mumbai)"],
            ["Account", "developer@tomitra.com"],
            ["EAS Account", "tomitra-apps"],
            ["App Store ID", "6762036036"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between py-1.5 border-b border-slate-800/50">
              <span className="text-xs text-slate-500">{k}</span>
              <code className="text-xs text-slate-300 font-mono">{v}</code>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function APISection() {
  return (
    <div>
      <SectionTitle icon={Server}>API Reference</SectionTitle>
      <p className="text-xs text-slate-400 mb-4">Base URL: <code className="text-brand-blue">https://tomitra-backend-187545700403.asia-south1.run.app</code></p>

      {[
        { group: "Feed", endpoints: [
          ["GET", "/api/v1/feed", "Main news feed (paginated, filtered)"],
          ["GET", "/api/v1/feed/local", "GPS-based local news"],
          ["GET", "/api/v1/feed/trending-topics", "3 trending topics"],
          ["GET", "/api/v1/search?q=...", "Full-text article search"],
        ]},
        { group: "Articles", endpoints: [
          ["GET", "/api/v1/articles/:id", "Article detail"],
          ["POST", "/api/v1/articles/:id/chat/stream", "AI chat (streaming SSE)"],
          ["GET", "/api/v1/articles/:id/insights", "Visual insight cards"],
          ["POST", "/api/v1/articles/:id/like", "Toggle like"],
          ["GET", "/api/v1/articles/:id/timeline", "Story timeline"],
        ]},
        { group: "User", endpoints: [
          ["GET/PUT", "/api/v1/user/profile", "Profile CRUD"],
          ["GET/POST/DEL", "/api/v1/user/bookmarks", "Bookmarks"],
          ["DELETE", "/api/v1/users/me", "Delete account"],
        ]},
        { group: "Other", endpoints: [
          ["GET", "/api/v1/briefings/latest", "Daily briefing"],
          ["POST", "/api/v1/feedback", "Submit feedback"],
          ["GET", "/health", "System health (public)"],
        ]},
      ].map(g => (
        <Card key={g.group} className="mb-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">{g.group}</h3>
          <table className="w-full"><tbody>
            <TableRow cells={["Method", "Endpoint", "Description"]} header />
            {g.endpoints.map((e, i) => <TableRow key={i} cells={e} />)}
          </tbody></table>
        </Card>
      ))}
    </div>
  );
}

function GovernanceSection() {
  return (
    <div>
      <SectionTitle icon={Shield}>Governance & Rules</SectionTitle>

      <p className="text-xs text-slate-400 mb-6 leading-relaxed">Tomitra follows strict governance rules across content quality, AI usage, deployment, security, and user experience. These rules are enforced in code, not just in prompts. Violating them breaks the product.</p>

      {/* Content Quality */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold text-red-400 mb-4">Content Quality (Non-Negotiable)</h3>
        <div className="space-y-3">
          {[
            { rule: "Transient AI failures are retried, not dropped", enforcement: "callClaude() retries every AI call up to 3x with jittered backoff on 429/5xx/timeout, never on 401/403/404. Without it a single Gemini rate-limit permanently cost the article, since ingestion files a failed summary as status=draft. Prod 2026-08-30: 25% of AI calls failed and 44% of articles never published (227 lost in 24h). Gemini remains the only provider.", file: "packages/ai-engine/src/clients/claude.ts" },
            { rule: "English summary: 60-90 words, never cut mid-sentence", enforcement: "enforce60Words() rejects under 60 (retry with the coaching prompt, then padSummary from source), keeps 60-90 ending on punctuation, and over 90 keeps only whole sentences that fit. If no whole-sentence cut clears 60 it returns null and the summary is regenerated: it never cuts inside a sentence. Ceiling raised 75 to 90 on 2026-09-25 because the old hard truncation at 72 words (with a period appended) ended roughly one in ten live cards on a fragment. The prompt targets 65-85 so the model lands inside the window.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Title: 10-15 words, two lines (5-8 until 2026-09-30)", enforcement: "Code: enforceTitle() truncates to the longest coherent cut at or under 15 words, never pads a short one up. No clickbait, no questions, no ellipsis. Raised because 8 words produced fragments missing the story's specific detail.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Hindi translation: min 50% of English word count", enforcement: "Code: translateSummary() checks word count. If NLLB output too short, falls back to Gemini LLM. Validates Devanagari script ratio ≥30%.", file: "packages/ai-engine/src/translator.ts" },
            { rule: "NLLB max_new_tokens must be 512", enforcement: "Was 256, caused Hindi truncation mid-sentence. Fixed 2026-04-18. Never reduce below 512.", file: "apps/translator/main.py" },
            { rule: "No ellipsis (…) or trailing dots (..) ever", enforcement: "Code: final cleanup regex strips all trailing dots/ellipsis, appends single period.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Models can't count words — code is the gate", enforcement: "The AI prompt says 65-85 words but the code enforcement in summarizer.ts is the real gate. Never remove retry/padSummary logic.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Expanded story version: 150-220 words, beyond the brief, grounded", enforcement: "generateDetailedSummary() enforces 130-word floor / 260 cap, strips ungrounded dates, receives the 60-word brief and must add what it lacks; grounded in publisher-page text fetched server-side (2026-07-19). Cached per article+language; grounding under 120 words yields nothing.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "बोध is generated, 10-34 words, and dropped if it fails", enforcement: "Until 2026-09-09 बोध was never generated: every surface split the 55-65 word summary and rendered its last sentence, so its length was whatever that sentence happened to be (readers reported one longer than the story body, and others of 4-5 words). It is now its own field with its own prompt, must add what the brief does not say, and runs the same grounding guards as the summary. enforceBodh() rejects under 10 or over 34 words, anything not ending in a terminator, and the 'This means/It signals' scaffolding openers. A failing बोध is DROPPED, not repaired — the article ships without one and clients fall back to splitting the summary, which is what every pre-2026-09-09 article does.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Trend Analysis follows the reader's language", enforcement: "The Hindi path made six serial translator calls (summary + five key points) and any one failing dropped the whole analysis to English — the reported 'sometimes displays in English'. Now one batch call, with per-piece fallback where a failed piece keeps its English rather than taking the other five down. New analyses are translated as soon as the English is generated, off the request path, so the first Hindi reader finds it cached (2026-09-12).", file: "apps/backend/src/services/trendingService.ts" },
            { rule: "Consistent card height, but never at the cost of readability", enforcement: "Hindi gained a ceiling (85 words, raised to 100 on 2026-09-25 with the English ceiling) (it had none, so it ran 60-89 against English's 15-word band, which is what uneven Hindi cards were). 85 rather than 75 because Devanagari averages 5.3 chars/word vs English 6.9 and sets ~15% wider, so 85 Hindi words fill about the same space as 75 English ones. Trimming is always to a sentence boundary. And after a retry has also missed, a COMPLETE 50-59 word summary is accepted rather than padded to 60 — padSummary splices raw source sentences and reads worse than a slightly shorter, well-formed summary (2026-09-12).", file: "packages/ai-engine/src/translator.ts" },
            { rule: "Hindi translation is validated on the Gemini path and asks for completeness", enforcement: "Production has no self-hosted translator, so Hindi is Gemini-only. summaryRejection()/titleRejection() reject wrong script, under-floor length or a dropped number, retry once, then throw so English is served. Title, summary and बोध are translated in one translateBundle() call, each field checked separately. Added 2026-09-26; the old prompt said exactly 50-70 words and compressed long text.", file: "packages/ai-engine/src/translator.ts" },
            { rule: "Summaries are 60-90 words in English, 60-120 in Hindi", enforcement: "Floor 60 since 2026-09-12 (it was 45 and 23% of live summaries landed under 55, which readers saw as cards that sometimes show fewer words). Ceiling 75 to 90 on 2026-09-25 (English) and 85 to 100 (Hindi, same ratio), then 100 to 120 on 2026-09-26 (production translates with Gemini only, which runs 1.2-1.3x the English word count when it keeps every sentence). Hindi has the same absolute floor, applied on the batch path ingestion actually uses. A complete 50-59 word summary is accepted after a retry rather than padded.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Ask Tomitra may answer from the web when the article cannot — and says so", enforcement: "The article pass is told to decline rather than guess; only then does a second, search-grounded call run, so cost lands only on questions the story does not cover. A web answer is discarded unless the search actually returned sources, because an ungrounded reply is the model's own memory — the stale answer this must not introduce. The search is pinned to the article's headline, date and entities so 'how many died?' cannot drift to a different incident. Every web answer is labelled in the UI with its source domains. Both the NOT_IN_ARTICLE marker and the model's own 'the article does not mention...' phrasing count as a decline; measured 8/8 correct on a live story (2026-09-09).", file: "packages/ai-engine/src/webAnswer.ts" },
            { rule: "Summaries are grounded in the publisher page, not the feed snippet", enforcement: "Ingestion already downloaded the article page for its og:image and threw the rest away, while the summarizer and Ask Tomitra both worked from the RSS contentSnippet — one or two sentences. That is why readers reported briefs missing context the source carries and Ask Tomitra unable to answer what the article plainly says (2026-09-09). One fetch now serves both and the extracted text is stored as original_content, so briefs, chat, Full Story and Deep Analysis all ground on the real article. Falls back to the snippet when extraction yields less (paywall stubs, consent pages); skipped for rolling pages.", file: "apps/backend/src/utils/articleText.ts" },
            { rule: "No dates or weekdays the source doesn't state", enforcement: "Code: stripUngroundedDates() removes month-day and weekday phrases absent from the source text (2026-07-18: model invented 'began his fast on March 6'). Prompt forbids invented specifics; code is the gate.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "Freshness gate: no articles older than 48h or undated", enforcement: "Ingestion drops articles whose source date is missing or older than 48h (2026-07-18: undated RSS items were stamped 'now' and March stories surfaced as breaking). Feed sorts by published_at.", file: "apps/backend/src/jobs/ingestNews.job.ts" },
            { rule: "Article image: publisher og:image first", enforcement: "Ingestion fetches og:image from the article page and uses feed/API image only as fallback (2026-07-18: aggregator images mismatched stories). No img-from-description extraction in RSS.", file: "apps/backend/src/jobs/ingestNews.job.ts" },
            { rule: "No image from rolling/live pages", enforcement: "Live blogs, live scores and 'top stories' digests get no image at all — their og:image is rewritten through the day, so a captured photo drifts away from the headline (2026-08-29: an RT top-stories digest put a Nepal flood photo on a Ukraine story). Card shows the source-logo placeholder instead.", file: "apps/backend/src/jobs/ingestNews.job.ts" },
            { rule: "An image used by another story is dropped", enforcement: "Publisher stock artwork and section-wide photos pass every per-URL check. If an image_url is already on a different article it is stripped from all of them (2026-08-29 audit: 1,686 of 18,734 rows shared an image). The junk-image filter now also guards the feed/API fallback, which previously bypassed it entirely.", file: "apps/backend/src/jobs/ingestNews.job.ts" },
            { rule: "Fuzzy dedup: word_similarity > 0.55 vs last 72h", enforcement: "pg_trgm check before the AI call rejects rephrased duplicates the exact content hash misses (2026-07-18: 25% of the feed was duplicates; threshold tuned on real pairs).", file: "apps/backend/src/jobs/ingestNews.job.ts" },
          ].map((r, i) => (
            <div key={i} className="bg-red-950/10 border border-red-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-200 mb-1">{r.rule}</div>
              <div className="text-[10px] text-slate-400 leading-relaxed">{r.enforcement}</div>
              <code className="text-[10px] text-slate-600 font-mono mt-1 block">{r.file}</code>
            </div>
          ))}
        </div>
      </Card>

      {/* AI Provider Rules */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold text-purple-400 mb-4">AI Provider Rules</h3>
        <div className="space-y-3">
          {[
            { rule: "Cascade: Gemini only (as of 2026-07-27)", enforcement: "Gemini 2.5 Flash-Lite is the sole active provider. Anthropic disabled via ANTHROPIC_FALLBACK_ENABLED=false (account had no credit balance — was failing every call, adding latency with zero benefit). Phi-3.5 was never deployed to tomitra-prod (deferred, ~$50-70/mo always-on cost). Safety net is now the content-quality gate in ingestNews.job.ts: a failed AI call means status='draft', never an unenforced published summary.", file: "packages/ai-engine/src/clients/claude.ts" },
            { rule: "Gemini calls send the Vertex Priority Paygo header", enforcement: "2026-07-30: sustained 429s under Standard Paygo's spend-history-based quota led to adding X-Vertex-AI-LLM-Shared-Request-Type: priority on the Gemini provider only (~1.8x price, no commitment, reversible). Known caveat from Google's own dev forum: this header has been reported to silently fall back to standard ON_DEMAND under some configs — monitor 429 rates, don't assume it's a permanent fix.", file: "packages/ai-engine/src/clients/claude.ts" },
            { rule: "Circuit breaker: 30min cooldown after auth/quota failure", enforcement: "401/403/402/404 → 30min cooldown (these mean \"will keep failing\"). 429 → no cooldown, changed 2026-08-04: with Gemini as the sole active provider, a blanket cooldown on rate-limit meant one unlucky request blacked out every unrelated feature (Ask Tomitra, Radio, briefings) for the whole window. Real incident 2026-08-04. Every call now attempts Gemini directly; only that call fails on a 429.", file: "packages/ai-engine/src/clients/claude.ts" },
            { rule: "Background AI calls back off when the aggregate 429 rate is already high", enforcement: "2026-09-16 to 09-30 incident: Vertex AI request volume ran 3-6x baseline (11,000-18,000/day vs ~3,000-5,500 normally, confirmed via Cloud Monitoring) while the aggregate 429 rate held 55-62% for two weeks — mostly callClaude()'s own 3 retries per failed call landing on an already-exhausted shared pool. recentRateLimitRate() now tracks the last 60s of non-streaming call outcomes; above 50% (with 8+ samples), callClaude() drops to 1 attempt instead of 3, since a draft article just retries next ingestion cycle anyway. Ask Tomitra's streaming retries are untouched — a reader is waiting on that one. Root cause (gemini-2.5-flash-lite has no fixed quota bucket in this project, confirmed via gcloud services quota list) needs a GCP support case, not yet filed.", file: "packages/ai-engine/src/clients/claude.ts" },
            { rule: "Every AI call must be logged to ai_call_logs", enforcement: "setAIMetricsCallback() in index.ts wires provider, model, latency, tokens, status to DB. Used by AI Analytics and Optimization Advisor.", file: "apps/backend/src/utils/aiLogger.ts" },
            { rule: "Chat identity: 'I'm Tomitra, your intelligent news companion'", enforcement: "System prompt forbids saying AI, Claude, Anthropic, assistant, bot. Identity is Tomitra.", file: "packages/ai-engine/src/prompts/explain.ts" },
            { rule: "Hindi: pure Devanagari only, no Roman transliteration", enforcement: "Script validation in translator.ts and languageInterpreter.ts. scriptRatio() checks ≥30% chars in Devanagari range (U+0900-097F). Rejects and retries if violated.", file: "packages/ai-engine/src/translator.ts" },
          ].map((r, i) => (
            <div key={i} className="bg-purple-950/10 border border-purple-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-200 mb-1">{r.rule}</div>
              <div className="text-[10px] text-slate-400 leading-relaxed">{r.enforcement}</div>
              <code className="text-[10px] text-slate-600 font-mono mt-1 block">{r.file}</code>
            </div>
          ))}
        </div>
      </Card>

      {/* Tomitra Stock Analyst Rules */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold text-teal-400 mb-4">Tomitra Stock Analyst Rules</h3>
        <div className="space-y-3">
          {[
            { rule: "No buy/sell/hold/target-price/stop-loss language, ever", enforcement: "Analysis only, never advice. SEBI (Research Analyst)/(Investment Adviser) Regulations require registration to publish that kind of content on Indian securities — this feature stays out of that territory by design. Sentiment is restricted to positive/negative/neutral/mixed, never a market call.", file: "packages/ai-engine/src/prompts/stockAnalyst.ts" },
            { rule: "No invented numbers or percentages", enforcement: "stripUngroundedNumbers() strips any figure (%, crore, lakh, etc.) not literally present in the source articles — same grounding principle as stripUngroundedDates, applied to the highest-risk hallucination surface for company/stock content.", file: "packages/ai-engine/src/summarizer.ts" },
            { rule: "No fabricated signal when there's no real news", enforcement: "A watched symbol with no matching articles that day renders as 'no significant news' rather than the model manufacturing importance.", file: "packages/ai-engine/src/prompts/stockAnalyst.ts" },
            { rule: "Disclaimer served from one place, not duplicated per client", enforcement: "getStockAnalystDisclaimer() returned directly by the digest API rather than hardcoded as static copy in the web page.", file: "packages/shared/src/constants/stockAnalyst.ts" },
            { rule: "Watchlist limited to a curated company list", enforcement: "Not open free-text — reliable matching against article entity extraction. Seeded in the same migration as the feature's tables.", file: "migrations/00035_stock_analyst.sql" },
            { rule: "Ask your Analyst chat carries the same hard rule", enforcement: "If a user directly asks 'should I buy/sell this?', the persona must explicitly decline and redirect to analysis, never quietly comply — this is the surface where a user is most likely to ask for advice directly.", file: "packages/ai-engine/src/prompts/stockAnalyst.ts" },
            { rule: "Chat falls back to a web search, but never for an advice request", enforcement: "Same two-pass shape as article-chat's web-search fallback: a question the watchlist/digest can't answer gets a live search instead of a flat decline. looksLikeAdviceRequest() checks the user's message in CODE before that second pass ever runs, so 'should I buy TCS?' can never reach a search — open web results very often carry someone else's buy/sell call. containsAdviceLanguage() is a second check on the search result itself, discarding it if one slipped through anyway.", file: "packages/ai-engine/src/stockAnalyst.ts" },
            { rule: "Mid-day breaking alerts reuse the existing article summary", enforcement: "No new AI call per user per story. Capped at 3/user/day, gated on the same importance score (>=55) every article already gets at ingestion — never fabricates urgency for a low-importance mention.", file: "apps/backend/src/services/stockBreakingAlertService.ts" },
          ].map((r, i) => (
            <div key={i} className="bg-teal-950/10 border border-teal-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-200 mb-1">{r.rule}</div>
              <div className="text-[10px] text-slate-400 leading-relaxed">{r.enforcement}</div>
              <code className="text-[10px] text-slate-600 font-mono mt-1 block">{r.file}</code>
            </div>
          ))}
        </div>
      </Card>

      {/* Deployment Rules */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold text-brand-blue mb-4">Deployment Rules</h3>
        <div className="space-y-3">
          {[
            { rule: "EAS update MUST include inline EXPO_PUBLIC_API_URL", enforcement: ".env has localhost URL. If shipped without inline override, production app hits local LAN. Broke production once on 2026-04-11." },
            { rule: "Android builds: LOCAL Gradle only, NOT EAS cloud", enforcement: "EAS cloud builds are slow and burn versionCode. Use local ./gradlew bundleRelease with keystore at apps/mobile/android/app/@fractalseer__tomitra-keystore.bak.jks." },
            { rule: "Android keystore must NEVER change", enforcement: "New keystore = Play Store rejects updates. Same keystore for ALL builds." },
            { rule: "iOS EAS builds need Firebase config plugin", enforcement: "ios/ is gitignored, EAS regenerates it. Plugin withFirebaseModularHeaders.js injects use_frameworks! :linkage => :static." },
            { rule: "versionCode bumped manually before each build", enforcement: "Cancelled EAS builds burn versionCode in Play Console. Always check Play Console before building." },
            { rule: "Never add androidKeyRestrictions to Firebase API key", enforcement: "Adding package+SHA restrictions to the Android API key breaks Phone Auth OTP. Google Sign-In security is at OAuth client level, not API key level." },
          ].map((r, i) => (
            <div key={i} className="bg-brand-blue/5 border border-brand-blue/15 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-200 mb-1">{r.rule}</div>
              <div className="text-[10px] text-slate-400 leading-relaxed">{r.enforcement}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Data & Ingestion Rules */}
      <Card className="mb-4">
        <h3 className="text-sm font-semibold text-yellow-400 mb-4">Data & Ingestion Rules</h3>
        <div className="space-y-3">
          {[
            { rule: "Deduplication by SHA-256(title + first 200 chars)", enforcement: "Content hash computed before AI call. DB check (SELECT 1 WHERE content_hash = X) skips duplicates instantly without wasting AI tokens." },
            { rule: "Article cap: 100 per ingestion run, applied AFTER dedup/freshness filtering", enforcement: "2026-08-01 fix: the cap used to slice the raw ~300-550 candidate batch down to 100 BEFORE any dedup/freshness check ran, so the slice was dominated by already-seen top headlines and ~85-90/100 got rejected every cycle while genuinely new RSS items further down were never even evaluated (real yield: 0-7 articles/run). Now the full batch is filtered first; the cap only bounds how many survive to the expensive AI-summarization step, matching what this rule always claimed." },
            { rule: "Local news: 7-day window", enforcement: "feedService.ts uses INTERVAL '7 days'. Widened from 24h on 2026-08-28 — only 7.5% of articles carry a location_city, so a 24h window returned zero results for most cities." },
            { rule: "postgres JSONB: always use db.json(), never JSON.stringify()", enforcement: "JSON.stringify() + ::jsonb double-serializes. Corrupted 99 rows on 2026-04-15 before caught." },
            { rule: "Every ingestion run logged to ingestion_logs", enforcement: "Status, duration, fetched/ingested/skipped/failed counts, source breakdown, error details." },
            { rule: "Scheduled jobs must hold a Postgres advisory lock before running", enforcement: "2026-08-01 fix: Cloud Run autoscaling (min 1 / max 10) runs the in-process node-cron scheduler once per instance — under normal traffic 2-3 instances are commonly alive, each firing the same cron tick independently (confirmed: 3 concurrent full-ingestion runs within 400ms of each other, most of each run's fetch wasted re-discovering what another instance had just inserted). pg_try_advisory_lock on a reserved connection now ensures only one instance runs a given job tick; others skip it outright. Self-releases if the holding instance dies mid-job.", file: "apps/backend/src/jobs/scheduler.ts" },
          ].map((r, i) => (
            <div key={i} className="bg-yellow-950/10 border border-yellow-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-200 mb-1">{r.rule}</div>
              <div className="text-[10px] text-slate-400 leading-relaxed">{r.enforcement}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Writing Style */}
      <Card>
        <h3 className="text-sm font-semibold text-emerald-400 mb-4">Writing & Style Rules</h3>
        <div className="space-y-3">
          {[
            { rule: "No em dashes (—) in any user-facing content", enforcement: "Use commas, periods, or 'and' instead. Em dashes are an AI tell." },
            { rule: "The brand is written lowercase, 'tomitra', everywhere user-facing", enforcement: "Including at the start of a sentence (2026-09-26). Exceptions: the legal entity 'Tomitra Technologies Private Limited', code identifiers, and data values such as provider = 'Tomitra' in ai_call_logs." },
            { rule: "No AI tells: 'delve', 'tapestry', 'landscape', 'realm'", enforcement: "Human-sounding copy. Simple, conversational language." },
            { rule: "Hindi: Devanagari only, never Roman transliteration", enforcement: "'लेकिन' not 'lekin'. Proper nouns (Trump, Mumbai, BJP) stay in English." },
            { rule: "Hindi style: conversational, not formal", enforcement: "'लेकिन' not 'परन्तु'. 'काम' not 'कार्य'. Like people actually speak." },
            { rule: "Summaries: lead with the fact, end with impact", enforcement: "Sentence 1: who/what/where/when. Sentence 2-3: details. Final sentence: why it matters." },
          ].map((r, i) => (
            <div key={i} className="bg-emerald-950/10 border border-emerald-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-200 mb-1">{r.rule}</div>
              <div className="text-[10px] text-slate-400 leading-relaxed">{r.enforcement}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function AdminSection() {
  return (
    <div>
      <SectionTitle icon={BarChart3}>Admin & Operations</SectionTitle>

      <Card className="mb-6">
        <h3 className="text-sm font-semibold text-slate-300 mb-4">Admin Dashboard Pages</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { page: "System Health", desc: "Backend + DB connectivity, version info" },
            { page: "Advisor", desc: "AI-generated recommendations from recent stats" },
            { page: "Ingestion", desc: "Cron run history, source breakdown, article counts" },
            { page: "Error Logs", desc: "Error logs with provider details" },
            { page: "Cost & Resources", desc: "Infrastructure cost estimates" },
            { page: "Users & Usage", desc: "DAU/WAU/MAU, feature usage, retention" },
            { page: "Feedback", desc: "User feedback entries" },
            { page: "AI Analytics", desc: "Provider latency, token usage, success rates" },
            { page: "Web Analytics", desc: "GA4 traffic for tomitra.com + news.tomitra.com" },
            { page: "LLM Playground", desc: "Test self-hosted models side-by-side" },
            { page: "Documentation", desc: "This page" },
            { page: "Tools", desc: "Manual triggers: ingestion, translation, re-summarize, backfills — each requires typing the tool name to confirm" },
          ].map(p => (
            <div key={p.page} className="bg-slate-800/30 rounded-lg p-3">
              <div className="text-xs font-semibold text-slate-300">{p.page}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{p.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Admin Auth</h3>
        <p className="text-xs text-slate-400 mb-2">Firebase Auth with email allowlist:</p>
        <div className="flex gap-2">
          <code className="text-[10px] bg-slate-800 px-2 py-1 rounded text-slate-400 font-mono">developer@tomitra.com</code>
        </div>
      </Card>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function DocsPage() {
  const [section, setSection] = useState<Section>("overview");

  const SECTIONS: Record<Section, () => JSX.Element> = {
    overview: OverviewSection,
    architecture: ArchitectureSection,
    tech: TechStackSection,
    ai: AISection,
    pipeline: PipelineSection,
    database: DatabaseSection,
    mobile: MobileSection,
    web: WebSection,
    infra: InfraSection,
    api: APISection,
    governance: GovernanceSection,
    admin: AdminSection,
  };

  const Component = SECTIONS[section];

  return (
    <div className="flex gap-6" style={{ minHeight: "calc(100vh - 100px)" }}>
      {/* Sidebar nav */}
      <div className="w-52 flex-shrink-0">
        <div className="sticky top-8">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-3 px-3">Documentation</div>
          <nav className="space-y-0.5">
            {NAV.map(item => (
              <button
                key={item.id}
                onClick={() => setSection(item.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  section === item.id
                    ? "bg-brand-blue/10 text-brand-blue font-medium"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-900"
                }`}
              >
                <item.icon size={15} />
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <Component />
      </div>
    </div>
  );
}
